import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Spin, Modal, Table } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PWH201Form.css';

import { usePWH201Mode } from './hooks/usePWH201Mode';
import { usePWH201Record } from './hooks/usePWH201Record';
import { usePWH201Navigation } from './hooks/usePWH201Navigation';
import { useLookupNames } from './hooks/useLookupNames';
import { usePWH201Approval } from './hooks/usePWH201Approval';
import { SharedActionBar } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwh201f1AcceptanceManifest } from '../../features/acceptance';
import type { ApprovalState } from '../../components/SharedActionBar';
import { useModulePermission } from '../../hooks/usePermission';
import Tab1Category from './components/Tab1Category';
import Tab2PaymentInfo from './components/Tab2PaymentInfo';
import Tab3InvoiceDetail from './components/Tab3InvoiceDetail';
import Tab4Remarks from './components/Tab4Remarks';
import { pwh201Api } from '../../services/pwh201Api';
import type { FH110, FH111, FH113, FH112, FH114, FH117, FH081, ApprovalHistory } from '../../services/pwh201Api';
import { isRecordLocked, getApprovalStatusInfo, calcInvoiceTotals, dateToInput } from './helpers';

const EMPTY_MASTER: Partial<FH110> = {
  fh110_01: '', fh110_11: new Date().toISOString(), fh110_12: 0, fh110_15: '',
  fh110_16: '', fh110_17: '', fh110_18: '', fh110_181: '', fh110_22: '',
  fh110_25: '', fh110_26: 1, fh110_27: '', fh110_28: 0, fh110_37: '',
  fh110_42: false, fh110_44: false, fh110_242: false, fh110_43: '',
  fh110_46: '', fh110_47: '', fh110_31: 0, fh110_32: 0, fh110_48: 0,
  fh110_80: '', fh110_90: 0, // ''=草稿（規格 pwh201f1；'N' 為草稿等價值）
};

const PWH201Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const perm = useModulePermission('pwh201f1');
  const { mode, toView, toAdd, toEdit, isEditing } = usePWH201Mode();
  const { record, loading, saving, load, save, remove } = usePWH201Record();
  const { hasPrev, hasNext, list, fetchList, goFirst, goLast, goPrev, goNext, setCurrentById } =
    usePWH201Navigation();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();

  const [formData, setFormData] = useState<Partial<FH110>>({ ...EMPTY_MASTER });
  const [expenseDetails, setExpenseDetails] = useState<Partial<FH111>[]>([]);
  const [invoiceDetails, setInvoiceDetails] = useState<Partial<FH113>[]>([]);
  const [loanOffsets, setLoanOffsets] = useState<Partial<FH112>[]>([]);
  const [prepaidOffsets, setPrepaidOffsets] = useState<Partial<FH114>[]>([]);
  const [agentOffsets, setAgentOffsets] = useState<Partial<FH117>[]>([]);
  const [paymentOffsets, setPaymentOffsets] = useState<Partial<FH081>[]>([]);
  const [activeTab, setActiveTab] = useState('1');
  const [approvalHistory, setApprovalHistory] = useState<ApprovalHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const locked = isRecordLocked(formData.fh110_80);
  const statusInfo = getApprovalStatusInfo(formData.fh110_80 || '');
  const { netTotal, taxTotal, grandTotal } = calcInvoiceTotals(invoiceDetails);

  // Approval state for SharedActionBar
  // FH110_80 規格 pwh201f1：''/'N'=草稿、'D'=送審中、'P'=已核准（'A'=會計審核中，核准系）
  const statusCode = (formData.fh110_80 || '').trim();
  const approval: ApprovalState = {
    statusCode,
    isApproved: statusCode === 'P' || statusCode === 'A',
    isPending: statusCode === 'D',
    isLocked: locked,
  };

  // Current index for navigation
  const currentIdx = record ? list.findIndex(r => r.fh110_01 === record.fh110_01) : -1;

  // Sync record to form state
  const syncFromRecord = useCallback((rec: FH110) => {
    setFormData({ ...rec });
    setExpenseDetails(rec.expense_details ? rec.expense_details.map(d => ({ ...d })) : []);
    setInvoiceDetails(rec.invoice_details ? rec.invoice_details.map(d => ({ ...d })) : []);
    setLoanOffsets(rec.loan_offsets ? rec.loan_offsets.map(d => ({ ...d })) : []);
    setPrepaidOffsets(rec.prepaid_offsets ? rec.prepaid_offsets.map(d => ({ ...d })) : []);
    setAgentOffsets(rec.agent_offsets ? rec.agent_offsets.map(d => ({ ...d })) : []);
    setPaymentOffsets(rec.payment_offsets ? rec.payment_offsets.map(d => ({ ...d })) : []);
  }, []);

  useEffect(() => {
    if (record) syncFromRecord(record);
  }, [record, syncFromRecord]);

  // Load record with name resolution
  const loadRecordWithNames = useCallback(async (id: string) => {
    const rec = await load(id);
    if (rec) {
      setCurrentById(id);
      await resolveAllNames(rec);
    }
  }, [load, setCurrentById, resolveAllNames]);

  // Approval hook
  const {
    handleSubmit: hookSubmit,
    handleApprove: hookApprove,
    handleReject: hookReject,
    handleWriteoff: hookWriteoff,
  } = usePWH201Approval({ record, isEditing, loadRecordWithNames });

  // Load approval history when modal opens
  useEffect(() => {
    if (showHistory && record) {
      pwh201Api.getApprovalHistory(record.fh110_01).then(setApprovalHistory).catch(() => {});
    }
  }, [showHistory, record]);

  // Navigation handlers
  const handleNav = useCallback(async (goFn: () => string | null) => {
    const id = goFn();
    if (id) await loadRecordWithNames(id);
  }, [loadRecordWithNames]);

  // Mode handlers
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fh110_11: new Date().toISOString() });
    setExpenseDetails([]); setInvoiceDetails([]);
    setLoanOffsets([]); setPrepaidOffsets([]); setAgentOffsets([]);
    clearNames();
    toAdd();
  }, [toAdd, clearNames]);

  const handleEdit = useCallback(() => { toEdit(); }, [toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      syncFromRecord(record);
      resolveAllNames(record);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setExpenseDetails([]); setInvoiceDetails([]);
    }
    toView();
  }, [record, toView, syncFromRecord, resolveAllNames]);

  const handleSave = useCallback(async () => {
    const masterToSave = {
      ...formData,
      fh110_12: netTotal,
      fh110_28: formData.fh110_28 ?? taxTotal,
      fh110_39: expenseDetails.length,
      fh110_391: invoiceDetails.length,
    };
    const result = await save(
      mode as 'add' | 'edit',
      masterToSave, expenseDetails, invoiceDetails,
    );
    if (result) {
      toView();
      await fetchList();
      setCurrentById(result.fh110_01);
      await resolveAllNames(result);
    }
  }, [formData, expenseDetails, invoiceDetails, netTotal, taxTotal, mode, save, toView, fetchList, setCurrentById, resolveAllNames]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除請款單 ${record.fh110_01}？`,
      okType: 'danger',
      onOk: async () => {
        const ok = await remove(record.fh110_01);
        if (ok) {
          toView();
          await fetchList();
          setFormData({ ...EMPTY_MASTER });
          setExpenseDetails([]); setInvoiceDetails([]);
          clearNames();
        }
      },
    });
  }, [record, remove, toView, fetchList, clearNames]);

  const setField = useCallback(<K extends keyof FH110>(field: K, value: FH110[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Initial load
  useEffect(() => {
    fetchList().then(() => {
      if (reviewId) {
        loadRecordWithNames(reviewId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchList]);

  const tabItems = [
    { key: '1', label: '請款類別', children: (
      <Tab1Category formData={formData} isEditing={isEditing} setField={setField} entryPersonName={formData.fh110_93 || ''} />
    )},
    { key: '2', label: '請款資訊', children: (
      <Tab2PaymentInfo
        formData={formData} names={names} isEditing={isEditing} isLocked={locked}
        setField={setField} resolveSingleName={resolveSingleName}
        netTotal={netTotal} taxTotal={taxTotal} grandTotal={grandTotal}
      />
    )},
    { key: '3', label: '請款明細/費用明細', children: (
      <Tab3InvoiceDetail
        invoices={invoiceDetails} isEditing={isEditing} isLocked={locked}
        masterNo={formData.fh110_01 || ''} onInvoicesChange={setInvoiceDetails}
      />
    )},
    { key: '4', label: '備註/沖帳', children: (
      <Tab4Remarks
        formData={formData} loanOffsets={loanOffsets} prepaidOffsets={prepaidOffsets}
        agentOffsets={agentOffsets} paymentOffsets={paymentOffsets}
        isEditing={isEditing} isLocked={locked}
        setField={setField} resolveSingleName={resolveSingleName} names={names}
      />
    )},
  ];

  return (
    <div className="pwh201-form">
      {/* Header bar */}
      <div className="pwh201-header-bar">
        <div className="field-group">
          <label>請款單號</label>
          <input value={formData.fh110_01 || '（新增後自動產生）'} readOnly disabled style={{ width: 160 }} data-contract-field="fh110_01" />
        </div>
        <div className="field-group">
          <label>請款日期</label>
          <input
            type="date"
            value={dateToInput(formData.fh110_11)}
            onChange={e => setField('fh110_11', (e.target.value ? e.target.value + 'T00:00:00Z' : null) as never)}
            disabled={!isEditing} data-contract-field="fh110_11"
          />
        </div>
        <div className="field-group">
          <label data-contract-field="fh110_80">審核狀態</label>
          <span className={`pwh201-status-badge ${statusInfo.color}`} data-contract-field="fh110_80">{statusInfo.label}</span>
        </div>
      </div>

      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Spin>

      {/* SharedActionBar */}
      <SharedActionBar
        mode={mode === 'view' ? 'browse' : mode} isEditing={isEditing} hasRecord={!!record}
        currentIdx={currentIdx >= 0 ? currentIdx : 0} recordCount={list.length}
        onNavFirst={() => handleNav(goFirst)} onNavPrev={() => handleNav(goPrev)}
        onNavNext={() => handleNav(goNext)} onNavLast={() => handleNav(goLast)}
        onAdd={handleAdd} onModify={handleEdit} onDelete={() => handleDelete()}
        onSave={handleSave} onCancel={handleCancel}
        approval={approval}
        onSubmit={hookSubmit} onApprove={hookApprove}
        onReject={hookReject} onWriteoff={hookWriteoff} onHistory={() => setShowHistory(true)}
        onExit={() => navigate('/erp')}
        canCreate={perm.canCreate} canEdit={perm.canEdit}
        canDelete={perm.canDelete} canApprove={perm.canApprove}
        acceptanceActionIds={{
          create: 'PWH201-ACT-CREATE', update: 'PWH201-ACT-UPDATE',
          delete: 'PWH201-ACT-DELETE', submit: 'PWH201-ACT-SUBMIT',
          approve: 'PWH201-ACT-APPROVE', reject: 'PWH201-ACT-REJECT',
          writeoff: 'PWH201-ACT-WRITEOFF',
        }}
      />
      <BatchAcceptanceBridge
        manifest={pwh201f1AcceptanceManifest}
        records={record ? [record] : []}
        selectedKeys={record?.fh110_01 ? [record.fh110_01] : []}
        keyOf={(item) => item.fh110_01}
        queryExact={(key) => pwh201Api.get(key)}
        lastApiResponse={record}
        loading={loading || saving}
        onAutoFill={() => list[0] && handleNav(goFirst)}
        autoFillDisabled={list.length === 0}
        autoFillReason="載入正式 API 已存在的第一筆應付請款單；儲存與審核仍走原表單 API。"
        screenDraft={{ master: formData, expense_details: expenseDetails, invoice_details: invoiceDetails, loan_offsets: loanOffsets, prepaid_offsets: prepaidOffsets, agent_offsets: agentOffsets, payment_offsets: paymentOffsets }}
      />

      {/* Approval History Modal */}
      <Modal
        title="審核歷史"
        open={showHistory}
        onCancel={() => setShowHistory(false)}
        footer={null}
        width={700}
      >
        <Table
          dataSource={approvalHistory}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            { title: '動作', dataIndex: 'action', width: 80 },
            { title: '執行人', dataIndex: 'actor_name', width: 100 },
            { title: '備註', dataIndex: 'comment' },
            { title: '舊狀態', dataIndex: 'old_status', width: 80 },
            { title: '新狀態', dataIndex: 'new_status', width: 80 },
            { title: '時間', dataIndex: 'created_at', width: 160,
              render: (v: string) => v ? new Date(v).toLocaleString('zh-TW') : '' },
          ]}
        />
      </Modal>
    </div>
  );
};

export default PWH201Form;
